import { RemoveCircleRounded } from '@mui/icons-material';
import { Box, Button, Divider, InputBase, Pagination } from '@mui/material';
import { color } from '@mui/system';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Types } from '../../enums/TypeEnums';
import {
  IIndex,
  IIndexSerie,
  useCreateIndexSerieMutation,
  useIndicesQuery,
  useUpdateIndexSerieMutation
} from '../../generated/graphql';
import { indexSerieWithThatNameExists } from '../../utils/clientUtils';
import { canUserEdit } from '../../utils/rightUtils';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import InputComponent from '../Common/Fields/InputComponent';
import DialogBox from '../Common/Layout/DialogBox';
import SearchInput from '../RecipientListWizard/SearchInput';

type IProps = {
  handleClose: (saved: boolean, isNew?: boolean, indexSerieInput?: IIndexSerie) => void;
  indexSerieInput: IIndexSerie | null;
};

export type IErrors = {
  indexSerieName: string | null;
};

const defaultErrorsObj: IErrors = {
  indexSerieName: null
};

type Index = IIndex & {
  isNew?: boolean;
};

const errorTexts = {
  indexSerieName: 'Index serie name is required'
};

const indexesPerPage = 9;
const firstPartOfMessage = `There are currently no indices in this serie.\n`;
const secondPartOfMessage = `Please select indices from the index menu on top.`;
const userMessage = firstPartOfMessage + secondPartOfMessage;

const indexSeriesForm: React.FunctionComponent<IProps> = ({
  handleClose,
  indexSerieInput
}: IProps) => {
  const [editable, setEditable] = useState(false);
  const [indexSerieName, setIndexSerieName] = useState(indexSerieInput?.name || '');
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [displayedIndexes, setDisplayedIndexes] = useState<Index[]>([]);
  const allIndexes = useIndicesQuery();
  const [indexList, setIndexList] = useState<Index[]>([]);
  const [pages, setPages] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [key, setKey] = useState('0');
  const [options, setOptions] = useState<IIndex[]>([]);
  const [createIndexSerie, createIndexSerieResult] = useCreateIndexSerieMutation();
  const [updateIndexSerie, updateIndexSerieResult] = useUpdateIndexSerieMutation();
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.IndexSeries);

  useEffect(() => {
    if (allIndexes.data && allIndexes.data.result) {
      const indices = allIndexes.data.result as IIndex[];
      if (indexSerieInput && indexSerieInput.indices && indexSerieInput.indices.length > 0) {
        const currentIndexIds = indexSerieInput.indices.map((i) => i?.id);
        setOptions(indices.filter((i) => !currentIndexIds.includes(i.id)));
      } else {
        setOptions(indices);
      }
    }
  }, [allIndexes.data]);

  useEffect(() => {
    if (indexSerieInput && indexSerieInput.indices) {
      const indexes = indexSerieInput.indices as Index[];
      const indexIds = indexSerieInput.indices.map((i) => i?.id);
      const data = allIndexes.data?.result as IIndex[];
      setIndexList(indexes as Index[]);
      setDisplayedIndexes(indexes.slice(0, indexesPerPage));
      setPages(indexes.length > indexesPerPage ? Math.ceil(indexes.length / indexesPerPage) : 0);
      setOptions(data.filter((i) => !indexIds.includes(i?.id)));
    }
  }, [indexSerieInput]);

  const handleCancel = () => {
    if (editable && indexSerieInput !== null) {
      setIndexSerieName(indexSerieInput?.name);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleIndexSerieNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value) {
      setFormErrors({ ...formErrors, indexSerieName: null });
    } else {
      setFormErrors({ ...formErrors, indexSerieName: errorTexts.indexSerieName });
    }
    setIndexSerieName(value);
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);
    const filtered = filterIndexes(indexList, text);
    setPages(filtered.length > indexesPerPage ? Math.ceil(filtered.length / indexesPerPage) : 0);
    setDisplayedIndexes(filtered.slice(0, indexesPerPage));
  };

  const filterIndexes = (input: IIndex[], text: string): IIndex[] => {
    if (text.length === 0) {
      return input;
    } else {
      const loweredText = text.toLowerCase();
      const filtered = input.filter((i) => i.name.toLowerCase().includes(loweredText));
      return filtered;
    }
  };

  const handleAddIndex = (_: unknown, indexObj: IIndex | null) => {
    if (indexObj !== null) {
      const isNew = indexSerieInput
        ? indexSerieInput?.indices.findIndex((i) => i?.id === indexObj.id) > -1
          ? false
          : true
        : true;
      const newData = [{ ...indexObj, isNew }, ...indexList];
      setIndexList(newData);
      const filteredData = filterIndexes(newData, searchText);
      if (newData.length > indexesPerPage) {
        setDisplayedIndexes(filteredData.slice(0, indexesPerPage));
      } else {
        setDisplayedIndexes(filteredData);
      }
      setOptions(options.filter((i) => i.id !== indexObj.id));
      setPages(
        filteredData.length > indexesPerPage ? Math.ceil(filteredData.length / indexesPerPage) : 0
      );
      setKey(indexObj.id);
    } else {
      setKey('0');
    }
  };

  const handleRemoveFromList = (index: Index) => {
    setKey('0');
    const newData = indexList.filter((i) => i.id !== index.id);
    setIndexList(newData);
    const filteredData = filterIndexes(newData, searchText);

    const start =
      (currentPage - ((currentPage - 1) * indexesPerPage == filteredData.length ? 2 : 1)) *
      indexesPerPage;
    setDisplayedIndexes(filteredData.slice(start, start + indexesPerPage));
    setPages(
      filteredData.length > indexesPerPage ? Math.ceil(filteredData.length / indexesPerPage) : 0
    );
    setOptions([index, ...options]);
    if (
      start + indexesPerPage == filteredData.length &&
      currentPage !== 1 &&
      currentPage == pages
    ) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    const start = (value - 1) * indexesPerPage;
    setDisplayedIndexes(indexList.slice(start, start + indexesPerPage));
    setCurrentPage(value);
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      indexSerieName: null
    };
    if (!indexSerieName) {
      hasErrors = true;
      errors.indexSerieName = errorTexts.indexSerieName;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      const indexSerieExists = indexSerieWithThatNameExists(
        indexSerieName as string,
        indexSerieInput?.id
      );
      if (indexSerieExists) {
        setFormErrors({
          ...formErrors,
          indexSerieName: indexSerieExists ? 'Index serie with that name already exists' : null
        });
      } else {
        handleSave(indexSerieInput === null ? 'create' : 'update');
      }
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createIndexSerie({
        variables: {
          input: {
            name: indexSerieName.trim(),
            index_ids: indexList.map((i) => Number(i.id))
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IIndexSerie);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Index serie created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateIndexSerie({
        variables: {
          id: indexSerieInput?.id as string,
          input: {
            name: indexSerieName.trim(),
            index_ids: indexList.map((i) => Number(i.id))
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IIndexSerie);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Index Serie Updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };
  return (
    <DialogBox
      id="create-new-index-serie"
      width={1280}
      onClose={handleCancel}
      title={indexSerieInput ? `${indexSerieInput?.name} Details` : 'Create Index Serie'}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '40px 22px',
          padding: '42px 80px',
          background: Colors.lightBlue
        }}
      >
        <InputComponent
          id="index-serie-name"
          label="Index Serie Name"
          value={indexSerieName || ''}
          onChange={handleIndexSerieNameChange}
          errorMsg={formErrors.indexSerieName}
          disabled={createIndexSerieResult.loading || updateIndexSerieResult.loading || !canEdit}
          additionalStyle={{ gridColumn: 'span 3', background: Colors.white, paddingLeft: '15px' }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '80px',
          background: Colors.lightBlue,
          padding: '25px 80px'
        }}
      >
        <SearchInput
          filter={handleSearchChange}
          id={'overview-step-search-input'}
          additionalStyleInputProps={{ boxSizing: 'unset' }}
        />
        <Divider
          orientation="vertical"
          style={{
            background: Colors.white,
            width: 2,
            paddingBottom: '7px',
            height: '36px',
            border: 'none'
          }}
        />
        <AutocompleteComponent
          id="add-index-autocomplete"
          label="Add index"
          options={options}
          key={key}
          disabled={createIndexSerieResult.loading || updateIndexSerieResult.loading || !canEdit}
          values={undefined}
          onChange={handleAddIndex}
          getOptionLabel={(option) => option.name}
          additionalStyle={{ border: 'none' }}
          isWhite={true}
        />
      </Box>
      {displayedIndexes.length > 0 ? (
        <Box
          sx={{
            minHeight: '450px',
            height: '450px',
            paddingTop: '30px'
          }}
        >
          {displayedIndexes.map((rec, index) => (
            <Box
              key={`${index}-${rec.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '3.5%',
                height: '40px',
                marginLeft: '6%',
                width: '88%',
                borderRadius: '9px',
                marginBottom: '8px',
                background: rec.isNew ? Colors.lightBlue : 'none',
                boxShadow: !rec.isNew ? '0px 3px 20px #12497F12' : 'none'
              }}
            >
              <Box
                sx={{
                  textAlign: 'left',
                  flexGrow: 1,
                  color: Colors.main
                }}
              >
                <InputBase
                  id={'index-' + rec.id + 'name'}
                  data-testid={'index-' + rec.id + 'name'}
                  value={rec.name}
                  disabled
                  fullWidth
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Overpass',
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: Colors.main
                    }
                  }}
                ></InputBase>
              </Box>
              <Box>
                <Button disabled={!canEdit} onClick={() => handleRemoveFromList(rec)}>
                  <RemoveCircleRounded style={{ color: Colors.skyBlue, width: 20, height: 21 }} />
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <span
          style={{
            color: Colors.main,
            fontSize: '16px',
            fontFamily: 'Overpass',
            height: '450px',
            letterSpacing: '0',
            opacity: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {userMessage}
        </span>
      )}
      {pages > 0 && (
        <Box sx={{ paddingLeft: '5%', paddingTop: '12px' }}>
          <Pagination
            id="index-serie-pagination"
            data-testid="index-serie-pagination"
            style={{ margin: 'auto' }}
            count={pages}
            page={currentPage}
            color="primary"
            onChange={handlePageChange}
          />
        </Box>
      )}
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: pages > 0 ? '56px' : '100px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-create-index-serie-button"
          disabled={createIndexSerieResult.loading || updateIndexSerieResult.loading}
          onClick={handleCancel}
          text={indexSerieInput === null ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="create-new-membership-button"
          disabled={createIndexSerieResult.loading || updateIndexSerieResult.loading || !canEdit}
          onClick={handleSubmit}
          text={indexSerieInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default indexSeriesForm;
